<template>
  <span class="pega-break">
    <applicant-print-title
      :entry-company-name="entryCompanyName"
      :user-name="headerUserName"
      :is-duplicated-entries="isDuplicatedEntries"
      :entry-form-type="entryForm"
      :pathway="pathway"
      :updated-date="resume.updatedDate"
    />
    <div class="boss-layout boss-layout-gap-between">
      <div>
        <div class="boss-resume-sheet-title pega-resume-sheet-title">履歴書</div>
      </div>
    </div>
    <div class="boss-layout-block">
      <div class="boss-table-wrap">
        <table class="boss-table-4 boss-table-4-responsive pega-th-color-important pega-print-resume-table">
          <tr>
            <th>応募求人</th>
            <td
              colspan="3"
              data-test="job-name"
            >{{ resume.jobName }}</td>
            <td
              rowspan="6"
              class="boss-table-4-plain boss-resume-sheet-photo-space-cell padding-bottom-1"
            >
              <div class="boss-resume-sheet-photo-space-layout">
                <div class="is-boss-only-pc">
                  <div class="boss-resume-sheet-photo-space" />
                </div>
                <div data-test="user-id">会員ID：{{ resume.userId }}</div>
              </div>
            </td>
          </tr>
          <tr>
            <th>応募日</th>
            <td
              colspan="3"
              data-test="entry-date"
            >{{ resume.entryDate | formatDateTime }}</td>
          </tr>
          <tr>
            <td
              class="boss-table-4-space"
              colspan="4"
            />
          </tr>
          <tr>
            <th>フリガナ</th>
            <td
              colspan="3"
              data-test="name-kana"
            >{{ userNameKana }}</td>
          </tr>
          <tr>
            <th>氏名</th>
            <td
              colspan="3"
              data-test="name"
            >{{ userName }}</td>
          </tr>
          <tr>
            <th>生年月日</th>
            <td data-test="birthday">{{ resume.birthday | formatDate }} {{ ageLabel }}</td>
            <th>性別</th>
            <td
              class="boss-nowrap"
              data-test="gender"
            >{{ resume.genderName }}</td>
          </tr>
          <tr>
            <th rowspan="2">現住所</th>
            <td
              colspan="4"
              data-test="zip"
            >{{ formatZip }}</td>
          </tr>
          <tr>
            <td
              colspan="4"
              data-test="address"
            >{{ formatAddress }}</td>
          </tr>
          <tr>
            <th>電話番号</th>
            <td data-test="tel">{{ resume.tel | parseBlankForDefaultValue }}</td>
            <th>携帯電話番号</th>
            <td
              colspan="2"
              class="boss-nowrap"
              data-test="mobile"
            >{{ resume.mobile | parseBlankForDefaultValue }}</td>
          </tr>
          <tr>
            <th>メールアドレス</th>
            <td
              data-test="email"
              class="pega-break"
            >{{ resume.email }}</td>
            <th>携帯メールアドレス</th>
            <td
              colspan="2"
              data-test="email-mobile"
              class="pega-break"
            >{{ resume.emailMobile | parseBlankForDefaultValue }}</td>
          </tr>
          <tr>
            <td
              class="boss-table-4-space"
              colspan="5"
            />
          </tr>
          <tr>
            <th colspan="5">学歴</th>
          </tr>
          <tr>
            <th data-test="graduated-date">{{ graduatedSchoolDate }}</th>
            <td
              colspan="4"
              data-test="graduated-school"
            >{{ graduatedSchool }}</td>
          </tr>
          <tr>
            <th colspan="5">職務経歴（概略）</th>
          </tr>
          <template v-if="resume.careerResumes && resume.careerResumes.length">
            <tr
              v-for="(careerResume, i) in resume.careerResumes"
              :key="i"
            >
              <td :data-test="'working-period-' + i">{{ workingPeriod(careerResume) }}</td>
              <td
                :data-test="'career-company-name-' + i"
                colspan="4"
              >{{ careerResume.companyName }}</td>
            </tr>
          </template>
          <template v-else>
            <tr>
              <td data-test="working-period-0">未登録</td>
              <td
                data-test="career-company-name-0"
                colspan="4"
              >未登録</td>
            </tr>
          </template>
          <tr>
            <td />
            <td
              colspan="4"
              class="boss-right"
            >以上</td>
          </tr>
          <tr>
            <td
              class="boss-table-4-space"
              colspan="5"
            />
          </tr>
          <tr>
            <th>学歴補足事項</th>
            <td
              colspan="4"
              data-test="educational-background-note"
              class="pega-pre-wrap"
            >{{ resume.educationalBackgroundNote | parseBlankForDefaultValue() }}</td>
          </tr>
          <tr>
            <th>就業状況</th>
            <td
              colspan="2"
              data-test="working-status-name"
            >{{ resume.workingStatusName | parseBlankForDefaultValue }}</td>
            <th>転職の回数</th>
            <td
              colspan="2"
              data-test="change-job-count"
            >{{ changeJobCount }}</td>
          </tr>
          <tr>
            <td
              class="boss-table-4-space"
              colspan="5"
            />
          </tr>
          <tr>
            <th>資格</th>
            <td
              colspan="4"
              data-test="licenses"
            >{{ licenses }}</td>
          </tr>
          <tr>
            <th>その他の資格</th>
            <td
              colspan="4"
              data-test="other-license"
              class="pega-pre-wrap"
            >{{ resume.otherLicense | parseBlankForDefaultValue() }}</td>
          </tr>
          <tr>
            <td
              class="boss-table-4-space"
              colspan="5"
            />
          </tr>
          <tr>
            <th>TOEIC</th>
            <td data-test="toeic">{{ resume.toeic | parseNumberValue('点') }}</td>
            <th>TOEFL</th>
            <td
              colspan="2"
              data-test="toefl"
            >{{ resume.toefl | parseNumberValue('点') }}</td>
          </tr>
          <tr>
            <th>英語スキル</th>
            <td data-test="english-skill">{{ resume.englishSkill | parseBlankForDefaultValue() }}</td>
            <th>英語以外の語学スキル</th>
            <td
              colspan="2"
              data-test="other-language-skill"
              class="pega-pre-wrap"
            >{{ resume.otherLanguageSkill | parseBlankForDefaultValue() }}</td>
          </tr>
          <tr>
            <td
              class="boss-table-4-space"
              colspan="5"
            />
          </tr>
          <tr>
            <th>自己PR</th>
            <td
              colspan="4"
              class="pega-pre-wrap"
              data-test="self-pr"
              v-text="selfPr"
            />
          </tr>
          <tr>
            <td
              class="boss-table-4-space"
              colspan="5"
            />
          </tr>
          <tr>
            <th>本人希望記入欄</th>
            <td colspan="4">
              <dl class="boss-definition-list">
                <dt>希望勤務地：</dt>
                <dd data-test="desired-prefecture">{{ desiredPrefecture }}</dd>
                <dt>希望年収：</dt>
                <dd data-test="desired-salary">{{ desiredSalary }}</dd>
                <dt>希望転職時期：</dt>
                <dd data-test="desired-change-period">{{ desiredChangePeriod }}</dd>
                <template v-for="(questionsAnswer, i) in resume.questionsAnswers">
                  <dt
                    :key="'dt-' + i"
                    :data-test="'dt-' + i"
                    class="pega-pre-wrap"
                    v-html="$sanitize(questionsAnswer.question) + '：'"
                  />
                  <dd
                    :key="'dd-' + i"
                    :data-test="'dd-' + i"
                    class="pega-pre-wrap"
                    v-html="$sanitize(questionsAnswer.answer)"
                  />
                </template>
              </dl>
            </td>
          </tr>
        </table>
      </div>
    </div>
  </span>
</template>

<script>
import ApplicantPrintTitle from '@/components/molecules/ApplicantPrintTitle'
import { createDate } from '@/lib/dateUtils.js'
import * as parse from '@/lib/parse'
import _ from 'lodash'

export default {
  components: {
    ApplicantPrintTitle
  },
  filters: {
    formatDate (date) {
      return createDate(date)?.format('YYYY年MM月DD日') ?? '未登録'
    },
    formatDateTime (date) {
      return createDate(date)?.format('YYYY年MM月DD日 HH時mm分') ?? ''
    },
    parseBlankForDefaultValue (label, suffix = '') {
      return label ? label + suffix : '未登録'
    },
    parseNumberValue (value, suffix = '') {
      return parse.numberValue(value, suffix)
    }
  },
  props: {
    resume: {
      type: Object,
      required: true
    },
    entryCompanyName: {
      type: String,
      default: ''
    },
    isDuplicatedEntries: {
      type: Boolean,
      default: false
    },
    entryForm: {
      type: String,
      default: ''
    },
    pathway: {
      type: Number,
      default: null
    },
    headerUserName: {
      type: String,
      default: ''
    }
  },
  computed: {
    userNameKana () {
      const userNameKanaList = [
        this.resume.lastnameKana,
        this.resume.firstnameKana
      ].filter(nameKana => nameKana)
      return userNameKanaList.length ? userNameKanaList.join(' ') : '未登録'
    },
    userName () {
      return [this.resume.lastname, this.resume.firstname].join(' ')
    },
    ageLabel () {
      const birthday = _.get(this.resume, 'birthday')
      return birthday ? `(満${parse.age(birthday)}歳)` : ''
    },
    graduatedSchoolDate () {
      if (this.resume.graduatedSchool == null) return '未登録'
      const graduateDate = createDate(`${this.resume.graduatedSchool.year}-${this.resume.graduatedSchool.month}`, 'YYYY-M', true)
      return graduateDate?.format('YYYY年MM月') ?? '未登録'
    },
    graduatedSchoolName () {
      if (this.resume.graduatedSchool == null) return ''
      const graduatedSchoolName = [
        this.resume.graduatedSchool.name,
        this.resume.graduatedSchool.department
      ].filter(name => name)
      return graduatedSchoolName.length ? `${graduatedSchoolName.join(' ')} 卒業` : ''
    },
    educationalBackgroundName () {
      const educationalBackgroundName = _.get(this.resume, 'educationalBackgroundName')
      if (_.isEmpty(educationalBackgroundName)) return ''
      // 学校名が設定されているか否か
      return this.graduatedSchoolName ? `(最終学歴:${educationalBackgroundName})` : `最終学歴:${educationalBackgroundName}`
    },
    graduatedSchool () {
      const graduatedSchool = [
        this.graduatedSchoolName,
        this.educationalBackgroundName
      ].filter(name => name)
      return graduatedSchool.length ? graduatedSchool.join(' ') : '未登録'
    },
    licenses () {
      const licenses = _.get(this.resume, 'licenses')
      return (licenses && licenses.length) ? licenses.join('、 ') : '未登録'
    },
    formatZip () {
      const zip = _.get(this.resume, 'zip')
      return zip ? `〒${zip}` : '未登録'
    },
    formatAddress () {
      const address = _.get(this.resume, 'address')
      return address || '未登録'
    },
    desiredPrefecture () {
      if (this.resume.isCasualInterviewRequest && !this.resume.isActiveUser) return '退会済みのため非表示'
      const prefectures = this.getDesiredSourceKey(this.resume, 'prefectures')
      return (prefectures && prefectures.length) ? prefectures.join(' 、 ') : '未登録'
    },
    desiredSalary () {
      if (this.resume.isCasualInterviewRequest && !this.resume.isActiveUser) return '退会済みのため非表示'
      const salary = this.getDesiredSourceKey(this.resume, 'salary')
      return salary ? `${salary}万円以上` : '未登録'
    },
    desiredChangePeriod () {
      const period = _.get(this.resume, 'desiredItem.changePeriod')
      return period || '未登録'
    },
    selfPr () {
      const pr = _.get(this.resume, 'selfPr')
      return pr || '未登録'
    },
    changeJobCount () {
      const val = _.defaultTo(_.get(this.resume, 'changeJobCount'), '未登録')
      return _.isNumber(val) ? `${val}回` : val
    }
  },
  methods: {
    workingPeriod (work) {
      if (!work.firstYear && !work.lastYear) return '未登録'
      // 9999が設定されている場合は'現在就業中'とする
      return `${this.toLocalDate(work.firstYear, work.firstMonth)}～${work.lastYear === 9999 ? '現在就業中' : this.toLocalDate(work.lastYear, work.lastMonth)}`
    },
    toLocalDate (year, month) {
      return createDate(`${year}-${month}`, 'YYYY-M')?.format('YYYY年MM月') ?? '未登録'
    },
    getDesiredSourceKey (resumeData, parameter) {
      let desiredSourceKey = 'desiredItem'
      if (resumeData.isCasualInterviewRequest) {
        desiredSourceKey = 'myPageDesiredItem'
      }
      return _.get(resumeData, `${desiredSourceKey}.${parameter}`)
    }
  }
}
</script>
